// src/components/Navbar.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Modal, Box, Card } from "@mui/material";
import { IoClose } from "react-icons/io5";
import './HomeV2.css';

// ✅ Import images for the modal
import cross1 from './images/crossover.png';
import organization from './images/organization.webp';
import individual from './images/individual.webp'
import mentalWellness from './images/mentalWellness.webp'

const BASE = process.env.PUBLIC_URL + '/homev2-assets';
const LOGO = `${BASE}/logo.png`;

const NAV_ITEMS = [
  {
    label: 'Individuals',
    children: [
      { label: 'Happiness Space', href: '/homehappinessnews' },
      { label: 'Happiness Meetup', href: '/meetups' },
      { label: 'Fundraiser', href: '/fundraiser' }
    ]
  },
  { label: 'Organisations', href: '/fororganization' },
  {
    label: 'Science',
    children: [
      { label: 'Bit Happier Lab', href: '/happinessresearch' },
      // ✅ Mark Data Marketplace as coming soon (internal flag only)
      { label: 'Data Marketplace', href: '/datamarketplace', isComingSoon: true }
    ]
  },
  { label: 'Art Insights', href: '/#art-insights' },
  { label: 'About Us', href: '/aboutus' }
];

// ✅ Modal style for Coming Soon (dark theme - copied from HomepageHeader)
const comingSoonModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: "95%",
  maxHeight: "90vh",
  overflowY: "auto",
  width: 636,
  bgcolor: "#000000e5",
  color: "white",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

// ✅ Modal style for Login/Signup (light theme)
const loginModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  border: "2px solid transparent",
  borderRadius: "12px",
  bgcolor: "#FFF5F7",
  boxShadow: "0px 4px 34px 0px #00000040",
  width: { xs: "95%", md: 600, lg: 1000 },
  outline: "none",
  py: 2,
  maxWidth: "1000px",
  margin: "auto",
  zIndex: 1300
};

function HomepageHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null);

  // ✅ Modal states
  const [openLoginModal, setOpenLoginModal] = useState(false);
  const [openSignUpModal, setOpenSignUpModal] = useState(false);
  const [dataMarketPlaceComingSoonModal, setDataMarketPlaceComingSoonModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);

  const navigate = useNavigate();
  const handleHashLinkClick = (e, href) => {
    if (href.includes('#')) {
      const pathPart = href.split('#')[0];
      const hashPart = href.split('#')[1];

      // If we're already on the target path, scroll smoothly
      if (window.location.pathname === pathPart || (pathPart === '/' && window.location.pathname === '/')) {
        const element = document.getElementById(hashPart);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (menuOpen && !e.target.closest('.hv2-navbar') && !e.target.closest('.hv2-mobile-nav')) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpen]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Helper to check if item is the coming soon Data Marketplace
  const isDataMarketplaceComingSoon = (item) => {
    return item.label === 'Data Marketplace' && item.isComingSoon;
  };

  const renderLink = (item, isMobile = false) => {
    const isAnchor = item.href && item.href.startsWith('#');
    const baseClass = isMobile ? 'hv2-mobile-nav-link' : 'hv2-nav-link';
    const clickHandler = isMobile ? () => setMenuOpen(false) : undefined;

    // ✅ If it's the coming soon Data Marketplace, open modal instead of navigating
    // 🔹 NO visual changes - inherits all existing styles from baseClass
    if (isDataMarketplaceComingSoon(item)) {
      return (
        <a
          href="#"
          className={baseClass}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (clickHandler) clickHandler();
            setDataMarketPlaceComingSoonModal(true);
          }}

        >
          {item.label}
          {/* 🔹 No badge, no extra text, no styling changes */}
        </a>
      );
    }

    return isAnchor ? (
      <a
        href={item.href}
        className={baseClass}
        onClick={(e) => {
          handleHashLinkClick(e, item.href);
          if (clickHandler) clickHandler();
        }}
      >
        {item.label}
      </a>
    ) : (
      <Link
        to={item.href}
        className={baseClass}
        onClick={(e) => {
          handleHashLinkClick(e, item.href);
          if (clickHandler) clickHandler();
        }}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <>
      <nav className={`hv2-navbar${scrolled ? ' hv2-scrolled' : ''}`}>
        <div className="hv2-container">
          <div className="hv2-nav-logo" onClick={() => navigate("/")}>
            <img src={LOGO} alt="Bit Happier" className="hv2-nav-logo-img" />
          </div>

          <ul className="hv2-nav-links">
            {NAV_ITEMS.map((item) => (
              <li key={item.label} className={`hv2-nav-item ${item.children ? 'hv2-has-dropdown' : ''}`}>
                {item.children ? (
                  <>
                    <a href={item.href} className="hv2-nav-link hv2-dropdown-trigger">
                      {item.label}
                      <span className="hv2-dropdown-icon">▾</span>
                    </a>
                    <ul className="hv2-dropdown-menu">
                      {item.children.map((child) => (
                        <li key={child.href}>{renderLink(child)}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  renderLink(item)
                )}
              </li>
            ))}
          </ul>

          <div className="hv2-nav-actions">
            <button
              className="hv2-btn hv2-btn-outline hv2-btn-md"
              onClick={() => setOpenLoginModal(true)}
            >
              Sign in
            </button>
            <button
              className="hv2-btn hv2-btn-primary hv2-btn-md"
              onClick={() => setOpenSignUpModal(true)}
            >
              Sign Up Free
            </button>
          </div>

          <button
            className="hv2-mobile-toggle"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((p) => !p)}
          >
            {menuOpen ? (
              <IoClose size={28} color="var(--text-dark)" />
            ) : (
              <>
                <span className="hv2-hamburger-line" />
                <span className="hv2-hamburger-line" />
                <span className="hv2-hamburger-line" />
              </>
            )}
          </button>
        </div>
      </nav>

      <div className={`hv2-mobile-nav${menuOpen ? ' hv2-open' : ''}`}>
        {NAV_ITEMS.map((item) => (
          <div key={item.label} className="hv2-mobile-nav-section">
            {item.children ? (
              <>
                <button
                  className="hv2-mobile-nav-link hv2-mobile-dropdown-trigger"
                  onClick={() => setOpenMobileDropdown(openMobileDropdown === item.label ? null : item.label)}
                >
                  {item.label}
                  <span className={`hv2-mobile-chevron ${openMobileDropdown === item.label ? 'hv2-chevron-rotated' : ''}`}>▾</span>
                </button>
                <div className={`hv2-mobile-dropdown ${openMobileDropdown === item.label ? 'hv2-dropdown-open' : ''}`}>
                  {item.children.map((child) => (
                    <div key={child.href} className="hv2-mobile-dropdown-item">
                      {renderLink(child, true)}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              renderLink(item, true)
            )}
          </div>
        ))}

        <div className="hv2-mobile-nav-actions">
          <button
            className="hv2-btn hv2-btn-outline hv2-btn-md"
            onClick={() => { setOpenLoginModal(true); setMenuOpen(false); }}
          >
            Sign in
          </button>
          <button
            className="hv2-btn hv2-btn-primary hv2-btn-md"
            onClick={() => { setOpenSignUpModal(true); setMenuOpen(false); }}
          >
            Sign Up Free
          </button>
        </div>
      </div>

      {/* ✅ DATA MARKETPLACE COMING SOON MODAL */}
      <Modal
        open={dataMarketPlaceComingSoonModal}
        onClose={() => setDataMarketPlaceComingSoonModal(false)}
        aria-labelledby="data-marketplace-modal"
        aria-describedby="data-marketplace-modal-description"
      >
        <Box sx={comingSoonModalStyle}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h2 className="h4 fw-bold mb-0 text-left w-100">
              Happiness Data Marketplace (Coming Soon)
            </h2>
            <span
              onClick={() => setDataMarketPlaceComingSoonModal(false)}
              role="button"
              className="ms-2"
              style={{ cursor: 'pointer' }}
            >
              <img src={cross1} height={20} alt="Close" />
            </span>
          </div>

          <p className="mb-4" style={{ color: '#ccc' }}>
            <strong>
              Bit Happier Announces the Community-Owned Happiness Data Marketplace
            </strong>
            <br />
            Bit Happier proudly introduces the Happiness Data Marketplace, a pioneering platform offering clinically backed well-being insights...
          </p>

          <h5 className="fw-semibold mb-2">
            Why the Community-Owned Happiness Data Marketplace?
          </h5>
          <p className="mb-4" style={{ color: '#ccc' }}>
            Mental health is more critical than ever, yet reliable, evidence-based happiness data remains fragmented...
          </p>

          <h5 className="fw-semibold mb-2">What Sets Us Apart?</h5>
          <ul className="mb-4" style={{ color: '#ccc', paddingLeft: '1.5rem' }}>
            <li className="mb-2">
              <img src="/grey-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle', color: "white" }} />{" "}
              Clinically-Backed Data
            </li>
            <li className="mb-2">
              <img src="/grey-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle' }} />{" "}
              AI-Driven Insights
            </li>
            <li className="mb-2">
              <img src="/grey-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle' }} />{" "}
              Decentralized & Community-Owned
            </li>
            <li className="mb-2">
              <img src="/grey-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle' }} />{" "}
              Transparent & Secure
            </li>
          </ul>

          <h5 className="fw-semibold mb-2">Who Benefits?</h5>
          <ul className="mb-4" style={{ color: '#ccc', paddingLeft: '1.5rem' }}>
            <li className="mb-2">
              <img src="/yellow-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle' }} />{" "}
              Individuals
            </li>
            <li className="mb-2">
              <img src="/yellow-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle' }} />{" "}
              Corporations & HR Leaders
            </li>
            <li className="mb-2">
              <img src="/yellow-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle' }} />{" "}
              Researchers & Health Professionals
            </li>
            <li className="mb-2">
              <img src="/yellow-check.svg" alt="check" style={{ width: "20px", marginRight: '8px', verticalAlign: 'middle' }} />{" "}
              AI & Tech Companies
            </li>
          </ul>

          <p className="mb-4" style={{ color: '#ccc' }}>
            <strong>Be Part of the Future of Happiness Research</strong>
            <br />
            Bit Happier is redefining happiness research and application through community-driven data ownership.
          </p>

          <div className="text-center">
            🚀 <strong>Join Us!</strong> The Community-Owned Happiness Data Marketplace is launching soon.
            <br />
            <a
              href="mailto:contact@bithappier.com"
              style={{ color: '#4da6ff', textDecoration: 'underline' }}
            >
              Sign Up here: contact@bithappier.com
            </a>
          </div>
        </Box>
      </Modal>

      {/* ✅ LOGIN MODAL (existing) */}
      <Modal
        open={openLoginModal}
        onClose={() => setOpenLoginModal(false)}
        aria-labelledby="login-modal"
        className="fitbit-modal"
      >
        <Box sx={loginModalStyle}>
          <div className={`px-6 ${isMobile ? 'py-2' : 'py-3'}`}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', minHeight: isMobile ? '40px' : '50px' }}>
              <h2 className="font-bold text-gray-900" style={{ fontSize: isMobile ? '20px' : '36px', margin: 0, textAlign: 'center' }}>Welcome to Bit Happier</h2>
              <button style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: "transparent", border: "none", cursor: "pointer", padding: isMobile ? '8px' : '16px', color: '#333' }} onClick={() => setOpenLoginModal(false)} aria-label="Close modal">
                <IoClose size={isMobile ? 24 : 32} />
              </button>
            </div>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', padding: '0 12px', alignItems: 'center' }}>
                {[
                  { src: individual, alt: 'Individual login', title: 'Login as an Individual', description: 'Start your personal happiness journey.', onClick: () => { navigate('/login', { state: { heading: 'Login as an Individual' } }); setOpenLoginModal(false); } },
                  { src: organization, alt: 'Organization login', title: 'Login as an Organization', description: 'Enhance workplace happiness and engagement.', onClick: () => { navigate('/corporate/login', { state: { heading: 'Login as an Organization' } }); setOpenLoginModal(false); } },
                  { src: mentalWellness, alt: 'Professional login', title: 'Login as a Mental Health Professional', description: 'Help others achieve mental wellness & happiness.', onClick: () => { navigate('/login#therapist', { state: { heading: 'Login as a Mental Health Professional' } }); setOpenLoginModal(false); } }
                ].map((card, index) => (
                  <Card key={index} sx={{ width: '100%', boxShadow: 3, borderRadius: 2, p: 1.5, border: '1px solid #e8e4f5', maxWidth: '350px', cursor: 'pointer' }} onClick={card.onClick}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                      <Box sx={{ bgcolor: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px', width: '80px', borderRadius: '50%' }}>
                        <img src={card.src} alt={card.alt} style={{ width: '80px', height: '80px', objectFit: 'contain' }} />
                      </Box>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <h3 className="font-semibold text-gray-900" style={{ fontSize: '16px' }}>{card.title}</h3>
                        <p className="text-xs text-gray-600" style={{ fontSize: '11px' }}>{card.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: '16px', overflowX: 'auto', paddingBottom: '8px', justifyContent: 'center' }}>
                {[
                  { src: individual, alt: 'Individual login', title: 'Login as an Individual', description: 'Start your personal happiness journey.', onClick: () => { navigate('/login', { state: { heading: 'Login as an Individual' } }); setOpenLoginModal(false); } },
                  { src: organization, alt: 'Organization login', title: 'Login as an Organization', description: 'Enhance workplace happiness and engagement.', onClick: () => { navigate('/corporate/login', { state: { heading: 'Login as an Organization' } }); setOpenLoginModal(false); } },
                  { src: mentalWellness, alt: 'Professional login', title: 'Login as a Mental Health Professional', description: 'Help others achieve mental wellness & happiness.', onClick: () => { navigate('/login#therapist', { state: { heading: 'Login as a Mental Health Professional' } }); setOpenLoginModal(false); } }
                ].map((card, index) => (
                  <Card key={index} sx={{ minWidth: 260, maxWidth: 260, width: 260, boxShadow: 3, borderRadius: 2, p: 3, cursor: 'pointer' }} onClick={card.onClick}>
                    <div className="flex flex-col gap-3">
                      <Box sx={{ bgcolor: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, height: '150px', width: '150px' }}>
                        <img src={card.src} alt={card.alt} className="object-contain" style={{ height: '150px', width: '150px' }} />
                      </Box>
                      <h3 className="font-semibold text-gray-900" style={{ fontSize: '18px' }}>{card.title}</h3>
                      <p className="text-xs text-gray-600" style={{ fontSize: '13px' }}>{card.description}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Box>
      </Modal>

      {/* ✅ SIGNUP MODAL (existing) */}
      <Modal
        open={openSignUpModal}
        onClose={() => setOpenSignUpModal(false)}
        aria-labelledby="signup-modal"
        className="fitbit-modal"
      >
        <Box sx={loginModalStyle}>
          <div className={`px-6 ${isMobile ? 'py-2' : 'py-3'}`}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', minHeight: isMobile ? '40px' : '50px' }}>
              <h2 className="font-bold text-gray-900" style={{ fontSize: isMobile ? '20px' : '36px', margin: 0, textAlign: 'center' }}>Welcome to Bit Happier</h2>
              <button style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: "transparent", border: "none", cursor: "pointer", padding: isMobile ? '8px' : '16px', color: '#333' }} onClick={() => setOpenSignUpModal(false)} aria-label="Close modal">
                <IoClose size={isMobile ? 24 : 32} />
              </button>
            </div>
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', padding: '0 12px', alignItems: 'center' }}>
                {[
                  { src: individual, alt: 'Individual signup', title: 'Sign Up as an Individual', description: 'Start your personal happiness journey.', onClick: () => { navigate('/signup', { state: { heading: 'Sign Up as an Individual' } }); setOpenSignUpModal(false); } },
                  { src: organization, alt: 'Organization signup', title: 'Sign Up as an Organization', description: 'Enhance workplace happiness and engagement.', onClick: () => { navigate('/corporatejoinus', { state: { heading: 'Sign Up as an Organization' } }); setOpenSignUpModal(false); } },
                  { src: mentalWellness, alt: 'Professional signup', title: 'Sign Up as a Mental Health Professional', description: 'Help others achieve mental wellness & happiness.', onClick: () => { navigate('/wellnessprofessionaljoin', { state: { heading: 'Sign Up as a Mental Health Professional' } }); setOpenSignUpModal(false); } }
                ].map((card, index) => (
                  <Card key={index} sx={{ width: '100%', boxShadow: 3, borderRadius: 2, p: 1.5, border: '1px solid #e8e4f5', maxWidth: '350px', cursor: 'pointer' }} onClick={card.onClick}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
                      <Box sx={{ bgcolor: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px', width: '80px', borderRadius: '50%' }}>
                        <img src={card.src} alt={card.alt} style={{ width: '80px', height: '80px', objectFit: 'contain' }} />
                      </Box>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <h3 className="font-semibold text-gray-900" style={{ fontSize: '16px' }}>{card.title}</h3>
                        <p className="text-xs text-gray-600" style={{ fontSize: '11px' }}>{card.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: '16px', overflowX: 'auto', paddingBottom: '8px', justifyContent: 'center' }}>
                {[
                  { src: individual, alt: 'Individual signup', title: 'Sign Up as an Individual', description: 'Start your personal happiness journey.', onClick: () => { navigate('/signup', { state: { heading: 'Sign Up as an Individual' } }); setOpenSignUpModal(false); } },
                  { src: organization, alt: 'Organization signup', title: 'Sign Up as an Organization', description: 'Enhance workplace happiness and engagement.', onClick: () => { navigate('/corporatejoinus', { state: { heading: 'Sign Up as an Organization' } }); setOpenSignUpModal(false); } },
                  { src: mentalWellness, alt: 'Professional signup', title: 'Sign Up as a Mental Health Professional', description: 'Help others achieve mental wellness & happiness.', onClick: () => { navigate('/wellnessprofessionaljoin', { state: { heading: 'Sign Up as a Mental Health Professional' } }); setOpenSignUpModal(false); } }
                ].map((card, index) => (
                  <Card key={index} sx={{ minWidth: 260, maxWidth: 260, width: 260, boxShadow: 3, borderRadius: 2, p: 3, cursor: 'pointer' }} onClick={card.onClick}>
                    <div className="flex flex-col gap-3">
                      <Box sx={{ bgcolor: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, height: '150px', width: '150px' }}>
                        <img src={card.src} alt={card.alt} className="object-contain" style={{ height: '150px', width: '150px' }} />
                      </Box>
                      <h3 className="font-semibold text-gray-900" style={{ fontSize: '18px' }}>{card.title}</h3>
                      <p className="text-xs text-gray-600" style={{ fontSize: '13px' }}>{card.description}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default HomepageHeader;